import { useEffect, useMemo } from 'react';

import { fromPromise } from 'xstate5';

import {
  type FetchPaymentMethodsParams,
  type FetchPaymentMethodsResponse,
  useOrderingMachine,
} from '@order/features-ordering';
import { useStripeConfirmHandler } from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { clearSharedLineItemAttribution } from '@order/shared-line-item';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { clearBundleAttribution } from '../../../../helpers/bundle-attribution';
import { useCloseBag } from '../../../../hooks';
import { ORDER_PLAYBACK_CONFIG } from '../../utils';
import { useAddUpsellToBag } from '../useAddUpsellToBag';
import { useBagCancelPreviousIntent } from '../useBagCancelPreviousIntent';
import { useBagLoyaltyRewards } from '../useBagLoyaltyRewards';
import { useBagTracking } from '../useBagTracking';
import { useChangeChannelOrLocation } from '../useChangeChannelOrLocation';
import { useConvertToGroupOrder } from '../useConvertToGroupOrder';
import { useCredits } from '../useCredits';
import { useDefaultPaymentMethod } from '../useDefaultPaymentMethod';
import { useFetchBagCartPolling } from '../useFetchBagCartPolling';
import { useFetchInitialData } from '../useFetchInitialData';
import { useFetchOrderPaymentState } from '../useFetchOrderPaymentState';
import { useFetchWantedTimes } from '../useFetchWantedTimes';
import { useGiftCards } from '../useGiftCards';
import { useHandleLeaveGroupOrderSuccess } from '../useHandleLeaveGroupOrderSuccess';
import { useLeaveGroupOrder } from '../useLeaveGroupOrder';
import { useLockGroupOrder } from '../useLockGroupOrder';
import { useNavigatePostOrder } from '../useNavigatePostOrder';
import { usePaymentIntentCreation } from '../usePaymentIntentCreation';
import { usePaymentMethods } from '../usePaymentMethods';
import { usePostOrderAppReview } from '../usePostOrderAppReview';
import { usePostOrderFetch } from '../usePostOrderFetch';
import { usePostOrderHistory } from '../usePostOrderHistory';
import { usePostOrderNotifications } from '../usePostOrderNotifications';
import { usePostOrderTracking } from '../usePostOrderTracking';
import { usePromoCode } from '../usePromoCode';
import { useRequestLogin } from '../useRequestLogin';
import { useRewards } from '../useRewards';
import { useSubmitOrder } from '../useSubmitOrder';
import { useUnlockGroupOrder } from '../useUnlockGroupOrder';
import { useUpdateLineItem } from '../useUpdateLineItem';

export const useOrderState = (): ReturnType<typeof useOrderingMachine> => {
  const isLoggedIn = useIsLoggedIn();
  const bagTracking = useBagTracking();
  const bagCartPollingInterval = useFeatureFlag('CELS-5406-order-cart-polling-interval');

  const closeBag = useCloseBag();
  const handleLeaveGroupOrderSuccess = useHandleLeaveGroupOrderSuccess({ closeBag });

  // ──────────────────────────────────────────────────────────────────────────

  const { fetchInitialData } = useFetchInitialData();
  const { fetchPaymentMethodsPromise: fetchPaymentMethods } = usePaymentMethods();
  const { fetchCredits } = useCredits();
  const { fetchWantedTimes } = useFetchWantedTimes();
  const { fetchBagCartPolling } = useFetchBagCartPolling();
  const { addUpsellToBag } = useAddUpsellToBag();
  const { applyGiftCard } = useGiftCards(fetchCredits);
  const { fetchRewards } = useBagLoyaltyRewards();
  const { applyReward, removeReward } = useRewards();
  const { setDefaultPaymentMethod } = useDefaultPaymentMethod();
  const { updateLineItem } = useUpdateLineItem();
  const { applyPromoCode } = usePromoCode();
  const { requestLogin } = useRequestLogin();
  const { submitOrder } = useSubmitOrder(setDefaultPaymentMethod);
  const { fetchOrderPaymentState } = useFetchOrderPaymentState();
  const { fetchStatePostOrder } = usePostOrderFetch();
  const { cancelPreviousIntent } = useBagCancelPreviousIntent();
  const { convertToGroupOrder, handleConvertToGroupOrderError } = useConvertToGroupOrder();
  const { leaveGroupOrder, handleLeaveGroupOrderError } = useLeaveGroupOrder();
  const { lockGroupOrder, handleLockGroupOrderError } = useLockGroupOrder();
  const { unlockGroupOrder, unlockCartInBackground, handleUnlockGroupOrderError } =
    useUnlockGroupOrder();

  const { navigatePostOrder } = useNavigatePostOrder();
  const { changeChannelOrLocation } = useChangeChannelOrLocation();
  const { handlePostOrderNotifications } = usePostOrderNotifications();
  const { handlePostOrderTracking } = usePostOrderTracking();
  const { handlePostOrderHistory } = usePostOrderHistory();
  const { handlePostOrderAppReview } = usePostOrderAppReview();

  const response = useOrderingMachine({
    isLoggedIn,
    orderSubmissionDelay: ORDER_PLAYBACK_CONFIG.orderSubmissionDelay,
    bagCartPollingInterval,
    bagTracking,
    navigatePostOrder,
    fetchInitialData,
    fetchPaymentMethods,
    fetchRewards,
    fetchWantedTimes,
    fetchBagCartPolling,
    fetchOrderPaymentState,
    fetchStatePostOrder,
    addUpsellToBag,
    updateLineItem,
    changeChannelOrLocation,
    requestLogin,
    applyReward,
    removeReward,
    applyPromoCode,
    applyGiftCard,
    cancelPreviousIntent,
    submitOrder,
    convertToGroupOrder,
    handleConvertToGroupOrderError,
    leaveGroupOrder,
    handleLeaveGroupOrderSuccess,
    handleLeaveGroupOrderError,
    lockGroupOrder,
    handleLockGroupOrderError,
    unlockGroupOrder,
    unlockCartInBackground,
    closeBag,
    handleUnlockGroupOrderError,
    handlePostOrderNotifications,
    handlePostOrderTracking,
    clearSharedLineItemAttribution,
    clearBundleAttribution,
    handlePostOrderHistory,
    handlePostOrderAppReview,
  });

  // ─── Stripe ──────────────────────────────────────────────────────────
  // The Stripe Confirm Handler requires a ref otherwise either the context
  // is stale or the SDK re-renders on every change to the properties below.

  const {
    wantedTime,
    selectedTip,
    cart,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
    flexPaymentMethod,
    dropoffLocation,
    shouldIncludeUtensils,
    registerStripeConfirmHandler,
    createPayment,
    failPayment,
    changePaymentMethod,
    cancelRequest,
  } = response;

  const { createPaymentIntent } = usePaymentIntentCreation({
    wantedTime,
    selectedTip,
    cart,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
    flexPaymentMethod,
    dropoffLocation,
    shouldIncludeUtensils,
    createPayment,
    failPayment,
    changePaymentMethod,
    cancelRequest,
  });

  const { stripeConfirmHandler } = useStripeConfirmHandler({ createPaymentIntent });

  useEffect(() => {
    registerStripeConfirmHandler(stripeConfirmHandler);
  }, [registerStripeConfirmHandler, stripeConfirmHandler]);

  // ─────────────────────────────────────────────────────────────────────

  return response;
};
